import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Route, Eye, Shield, Lock, Waves, Hand, LifeBuoy, MessageCircleHeart, Info, ArrowLeft, Home, Mail, Check, BookmarkCheck, UserPlus, LogIn } from "lucide-react";
import FeedbackForm from "@/components/FeedbackForm";
import { ReportEmailForm } from "@/components/ReportEmailForm";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/analytics";

const ACCENT = "#4FA3D9";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { trust: Eye, boundary: Shield, privacy: Lock, pressure: Waves, protective: Hand, help: LifeBuoy };
const fade = { hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } } };

// Schema-v2 (Simulation #2) Practice Report. Renders ONLY the deterministic content object built
// server-side; observed behaviour and the child's later self-report are kept visually separate.
// Props: summary (frozen v2 report), simTitle, onBack/onHome, and — for the just-completed flow —
// simId + sessionId + onRestart, which enable email / claim CTA / feedback / Practise again.
export const PracticeReportV2 = ({ summary, simTitle, onBack, onHome, simId, sessionId, onRestart }) => {
  const c = summary?.content || {};
  const title = simTitle || summary?.title || summary?.scenario || "Practice Report";
  const date = (summary?.created_at || "").slice(0, 10);
  const reportId = summary?.report_id;
  const viewed = useRef(false);
  useEffect(() => {
    if (viewed.current || !reportId) return;
    viewed.current = true; // report_viewed exactly once per mount, after the frozen report actually renders
    track("report_viewed", null, { simulation_id: simId || summary?.simulation_id, session_id: sessionId, report_id: reportId });
  }, [reportId, simId, sessionId, summary?.simulation_id]);
  return (
    <div className="min-h-[100dvh] w-full overflow-y-auto bg-[#0a0a0a] px-5 py-14 text-white sm:px-6 sm:py-16" data-testid="practice-report-v2">
      <motion.div initial="hidden" animate="show" transition={{ staggerChildren: 0.08 }} className="mx-auto max-w-2xl">
        {(onBack || onHome) && (
          <div className="mb-10 flex items-center gap-3 text-sm text-white/55">
            {onBack && <button onClick={onBack} data-testid="report-v2-back" className="inline-flex items-center gap-2 transition-colors hover:text-white/90"><ArrowLeft size={16} /> Back</button>}
            {onBack && onHome && <span className="text-white/20">·</span>}
            {onHome && <button onClick={onHome} data-testid="report-v2-home" className="inline-flex items-center gap-2 transition-colors hover:text-white/90"><Home size={15} /> Home</button>}
          </div>
        )}
        <motion.header variants={fade}>
          <p className="text-xs font-semibold tracking-[0.16em]" style={{ color: ACCENT }}>ADAPTIENCE™ PRACTICE REPORT</p>
          <h1 className="font-display mt-4 text-3xl font-semibold sm:text-4xl" data-testid="report-v2-title">{title}</h1>
          {date && <p className="mt-2 text-xs text-white/40" data-testid="report-v2-date">Report date: {date}</p>}
          {c.about?.text && <p className="mt-6 text-base leading-relaxed text-white/60" data-testid="report-v2-about">{c.about.text}</p>}
        </motion.header>

        {c.digest && <Digest g={c.digest} />}
        {c.try_together && <TryTogether t={c.try_together} />}
        {c.journey?.items?.length > 0 && (
          <motion.section variants={fade} className="mt-12" data-testid="report-v2-journey">
            <SectionTitle icon={Route} text={c.journey.title} />
            <ol className="mt-4 space-y-2.5">
              {c.journey.items.map((it, i) => (
                <li key={it.stage} className="flex items-baseline gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3" data-testid={`journey-item-${it.stage}`}>
                  <span className="text-xs tabular-nums text-white/35">{i + 1}</span>
                  <span className="text-[15px] font-medium">{it.label}</span>
                  {it.detail && <span className="ml-auto text-right text-sm text-white/55">{it.detail}</span>}
                </li>
              ))}
            </ol>
            {c.reflection_intro?.lines?.length > 0 && <SelfReport label={c.reflection_intro.label} lines={c.reflection_intro.lines} note={c.reflection_intro.note} testId="report-v2-reflection-intro" />}
          </motion.section>
        )}

        {(c.dimensions || []).map((d) => <Dimension key={d.key} d={d} tag={c.not_reached_tag} />)}

        {c.important_context?.text && (
          <motion.section variants={fade} className="mt-12 rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid="report-v2-important-context">
            <SectionTitle icon={Info} text={c.important_context.title} small />
            <p className="mt-3 text-sm leading-relaxed text-white/65">{c.important_context.text}</p>
          </motion.section>
        )}

        {c.evidence_note && <motion.p variants={fade} className="mt-8 text-xs leading-relaxed text-white/40" data-testid="report-v2-evidence-note">{c.evidence_note}</motion.p>}

        {sessionId && reportId && <ReportActions reportId={reportId} sessionId={sessionId} onRestart={onRestart} />}
      </motion.div>
    </div>
  );
};

// Post-report platform actions (existing shared infrastructure): email PDF (v2 selected server-side),
// optional account claim (proactive single-use token stash), universal feedback, Practise again.
const ReportActions = ({ reportId, sessionId, onRestart }) => {
  const navigate = useNavigate();
  const { account, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const claimIssued = useRef(false);
  useEffect(() => {
    if (loading || account || claimIssued.current) return;
    claimIssued.current = true;
    (async () => {
      try {
        const r = await fetch(`${API}/simulation/claim-token`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sessionId }) });
        if (r.ok) { const d = await r.json(); if (d.claimable && d.claim_token) sessionStorage.setItem("adaptience_claim", d.claim_token); }
      } catch { /* claim is optional; never blocks the report */ }
    })();
  }, [loading, account, sessionId]);
  return (
    <>
      <motion.div variants={fade} className="mt-12 rounded-3xl border border-white/10 bg-white/[0.04] p-6 sm:p-8" data-testid="keep-report">
        <div className="mb-2 flex items-center gap-2.5" style={{ color: ACCENT }}><Mail size={16} /><span className="text-xs font-semibold uppercase tracking-[0.2em]">Keep Your Practice Report</span></div>
        {sent ? (
          <div className="mt-3 flex items-center gap-3 text-white/85" data-testid="report-email-sent"><span className="flex h-9 w-9 items-center justify-center rounded-full" style={{ background: ACCENT }}><Check size={18} /></span><p className="text-base">Sent. Check your inbox for your Practice Report.</p></div>
        ) : (
          <ReportEmailForm reportId={reportId} onSent={(to) => { setEmail(to); setSent(true); }} />
        )}
      </motion.div>
      {!account ? (
        <motion.div variants={fade} className="mt-8 rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8" data-testid="claim-cta">
          <div className="mb-2 flex items-center gap-2.5" style={{ color: ACCENT }}><BookmarkCheck size={16} /><span className="text-xs font-semibold uppercase tracking-[0.2em]">Keep this practice in My Adaptience</span></div>
          <p className="mt-2 text-base leading-relaxed text-white/60">Sign in or create an account to add this experience to your Practice History.</p>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <button onClick={() => navigate("/signin")} data-testid="claim-create-account-btn" className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold uppercase tracking-[0.14em] text-white" style={{ background: ACCENT }}><UserPlus size={16} /> Create account</button>
            <button onClick={() => navigate("/signin")} data-testid="claim-signin-btn" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-white/85 hover:bg-white/5"><LogIn size={16} /> Sign in</button>
          </div>
          <p className="mt-3 text-xs text-white/40">Optional — your report, email and feedback all work without an account.</p>
        </motion.div>
      ) : (
        <motion.div variants={fade} className="mt-8 flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4" data-testid="claim-saved">
          <span className="flex h-8 w-8 items-center justify-center rounded-full" style={{ background: ACCENT }}><BookmarkCheck size={16} /></span><p className="text-sm text-white/80">Saved to My Adaptience.</p>
        </motion.div>
      )}
      <FeedbackForm sessionId={sessionId} prefillEmail={sent ? email.trim() : ""} />
      {onRestart && (
        <motion.button variants={fade} onClick={onRestart} data-testid="report-restart-btn" className="mt-10 inline-flex items-center gap-3 rounded-full border border-[#4FA3D9] px-7 py-3.5 text-sm font-medium tracking-wide text-[#4FA3D9] transition-colors hover:bg-[#4FA3D9] hover:text-white focus-visible:bg-[#4FA3D9] focus-visible:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a]">Practise again</motion.button>
      )}
    </>
  );
};

const SectionTitle = ({ icon: Icon, text, small }) => (
  <div className="flex items-center gap-2.5">
    <Icon size={small ? 15 : 17} style={{ color: ACCENT }} />
    <h2 className={small ? "text-sm font-semibold tracking-wide text-white/85" : "text-base font-semibold tracking-wide md:text-lg"}>{text}</h2>
  </div>
);

const Field = ({ label, text, testId }) => text ? (
  <div className="mt-4" data-testid={testId}>
    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/45">{label}</p>
    <p className="mt-1.5 text-base leading-relaxed text-white/85">{text}</p>
  </div>
) : null;

const SelfReport = ({ label, lines, note, testId }) => (
  <div className="mt-5 rounded-2xl border border-dashed border-[#4FA3D9]/35 bg-[#4FA3D9]/[0.06] p-4" data-testid={testId}>
    <p className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: ACCENT }}>{label}</p>
    {lines.map((l, i) => <p key={i} className="mt-2 text-[15px] leading-relaxed text-white/85">{l}</p>)}
    {note && <p className="mt-3 text-xs italic leading-relaxed text-white/45">{note}</p>}
  </div>
);

const Dimension = ({ d, tag }) => {
  const Icon = ICONS[d.key] || Eye;
  return (
    <motion.section variants={fade} className="mt-12" data-testid={`dimension-${d.key}`} data-reached={d.reached ? "true" : "false"}>
     <div className={d.reached ? "" : "opacity-60"} data-testid={`dimension-${d.key}-body`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <SectionTitle icon={Icon} text={d.title} />
        {!d.reached && <span className="rounded-full border border-white/15 px-2.5 py-0.5 text-[11px] font-medium tracking-wide text-white/55" data-testid={`dimension-${d.key}-not-reached`}>{tag}</span>}
      </div>
      {d.reached ? (
        <>
          <Field label="What happened" text={d.what_happened} testId={`dimension-${d.key}-what`} />
          <Field label="What this practice revealed" text={d.revealed} testId={`dimension-${d.key}-revealed`} />
          <Field label="Practise next" text={d.practise_next} testId={`dimension-${d.key}-practise`} />
        </>
      ) : (
        <p className="mt-3 text-sm leading-relaxed text-white/60" data-testid={`dimension-${d.key}-what`}>{d.what_happened}</p>
      )}
      {d.self_report?.text && <SelfReport label={d.self_report.label} lines={[d.self_report.text]} note={d.self_report.note} testId={`dimension-${d.key}-self-report`} />}
     </div>
    </motion.section>
  );
};

// Parent digest: deterministic fields from the report content object (no scoring, no inference).
const Digest = ({ g }) => (
  <motion.section variants={fade} className="mt-10 rounded-3xl border border-[#4FA3D9]/30 bg-[#4FA3D9]/[0.06] p-5 sm:p-6" data-testid="report-v2-digest">
    <SectionTitle icon={Info} text={g.title} />
    <dl className="mt-4 grid gap-4 sm:grid-cols-3">
      {[["happened", g.happened_label, g.happened], ["handled", g.handled_label, g.handled], ["practise", g.practise_label, g.practise]].map(([k, l, v]) => v && (
        <div key={k} data-testid={`digest-${k}`}><dt className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/45">{l}</dt><dd className="mt-1.5 text-[15px] leading-relaxed text-white/85">{v}</dd></div>
      ))}
    </dl>
    {g.next && <p className="mt-4 text-sm text-[#9fd3f5]" data-testid="digest-next">{g.next}</p>}
  </motion.section>
);

const TryTogether = ({ t }) => (
  <motion.section variants={fade} className="mt-12 rounded-3xl border border-white/10 bg-white/[0.04] p-5 sm:p-6" data-testid="report-v2-try-together">
    <SectionTitle icon={MessageCircleHeart} text={t.title} />
    <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/45">Ask</p>
    <p className="mt-1.5 text-base leading-relaxed text-white/85">“{t.ask}”</p>
    <p className="mt-4 text-base leading-relaxed text-white/70">{t.practise} For example: <span className="text-white/85">“{t.example}”</span></p>
    <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/45">Parent commitment</p>
    <p className="mt-1.5 text-base leading-relaxed text-white/85">“{t.commit}”</p>
    {t.directions?.length > 0 && (
      <div className="mt-6 border-t border-white/10 pt-5" data-testid="report-v2-directions">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/45">{t.directions_title}</p>
        <ul className="mt-3 space-y-3">
          {t.directions.map((dr) => (
            <li key={dr.target} className="flex items-start gap-3" data-testid={`direction-${dr.target}`}>
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: ACCENT }} />
              <p className="text-[15px] leading-relaxed text-white/80"><span className="font-medium text-white">{dr.title}.</span> {dr.line}</p>
            </li>
          ))}
        </ul>
      </div>
    )}
  </motion.section>
);

export default PracticeReportV2;
